import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Globe2, Network, ShieldCheck, Award, Compass, MapPin, Wifi } from "lucide-react";
import { parseDelimitedItems, parseKeyValueMap, parseRecordItems } from "@/lib/pageContent";
import { usePublicSettings } from "@/hooks/usePublicSiteData";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";

export default function About() {
  const { language } = useSiteLanguage();
  const { data: settings } = usePublicSettings();
  const ui = getPublicUiCopy(language, "about");
  const founderName = settings?.founder_name || ui.founderProfileName;
  const founderTitle = settings?.founder_profile_title || "Founder of R.S. International";

  const stats = parseRecordItems(
    settings?.about_page_stats || "30+|Countries served\n500+|Verified suppliers\n24h|Quote turnaround\nISO 9001|Quality certified",
    ["num", "label"]
  );
  const values = parseDelimitedItems(
    settings?.about_page_values_items ||
      "Verified Sourcing|Every supplier in our network is audited for compliance, capacity, and quality control before they appear in your quote.\nDirect Mill Access|We cut out middlemen so you get factory-direct pricing with full traceability on every shipment.\nDoor-to-Door Logistics|From FOB negotiation to customs clearance to last-mile delivery, our team handles the full chain."
  );
  const cardLabels = parseKeyValueMap(settings?.about_page_card_labels || "");

  return (
    <>
      {/* Hero */}
      <section className="bg-[var(--rsi-bg-subtle)] py-20 lg:py-28" data-testid="about-hero">
        <div className="container-rsi grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="badge-navy">
              <Globe2 className="h-3.5 w-3.5" /> {settings?.about_page_badge || "Founder & Leadership"}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-4 leading-[1.02] text-[var(--rsi-navy)]">
              {settings?.about_page_title || "Built by a founder,"}<br />
              <span className="text-[var(--rsi-purple)]">{settings?.about_page_highlight || "carried forward by leadership."}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-700">
              {settings?.about_page_intro_1 ||
                "Shankar Kumar Howlader founded R.S. International in 1985 and established the values that shaped the organization from the beginning: discipline, professional integrity, and trust in long-term business relationships."}
            </p>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-gray-700">
              {settings?.about_page_intro_2 ||
                "His legacy continues through a business story that began with medical imports, early institutional clients, and a founder's commitment to honest work. The full life story is available on the dedicated founder profile page."}
            </p>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-gray-700">
              {settings?.about_page_intro_3 ||
                "Today, that same foundation is carried forward by Engr. Dibakar Howlader, the current owner and Chief Executive Officer, who continues to expand the company with reliability, structure, and modern leadership."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
                {settings?.about_page_primary_cta || "Talk to Trade Desk"}
              </Link>
              <Link to="/trust-center" className="btn-ghost" data-testid="about-cta-trust">
                {settings?.about_page_secondary_cta || "View Certifications"}
              </Link>
            </div>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[var(--rsi-purple)]/20 to-transparent rounded-3xl blur-2xl" />
              <img
                src={settings?.about_page_hero_image_url || "https://images.unsplash.com/photo-1759826350352-c5b0b77729bd?w=1200&q=85"}
                alt={ui.heroImageAlt}
                className="relative rounded-2xl shadow-2xl shadow-purple-900/20 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white" data-testid="about-stats">
        <div className="container-rsi">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rsi-card p-6"
              >
                <div className="font-display text-4xl font-black tracking-tighter text-[var(--rsi-purple)]">
                  {s.num}
                </div>
                <div className="mt-1 text-sm text-gray-600">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[var(--rsi-bg-subtle)]" data-testid="about-values">
        <div className="container-rsi">
          <div className="max-w-2xl">
            <span className="badge-purple">
              <Award className="h-3.5 w-3.5" /> {settings?.about_page_values_badge || "What we do differently"}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
              {settings?.about_page_values_title || "How we earn the trust of B2B buyers, every shipment."}
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <div key={i} className="rsi-card p-7" data-testid={`about-value-${i}`}>
                <div className="h-12 w-12 rounded-xl grid place-items-center bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)] mb-4">
                  {i % 3 === 0 ? <ShieldCheck className="h-6 w-6" /> : i % 3 === 1 ? <Network className="h-6 w-6" /> : <Compass className="h-6 w-6" />}
                </div>
                <h3 className="font-display font-extrabold text-xl tracking-tight text-[var(--rsi-navy)]">
                  {v.title}
                </h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 bg-[var(--rsi-bg-subtle)]" data-testid="about-founder">
        <div className="container-rsi">
          <div className="mx-auto max-w-5xl">
            <div className="grid min-[680px]:grid-cols-2 gap-6 xl:gap-7">
              <div className="relative overflow-hidden rounded-[30px] border border-white/10 bg-[#071017] shadow-2xl shadow-black/25">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_25%,rgba(0,209,255,0.18),transparent_32%),radial-gradient(circle_at_80%_18%,rgba(102,51,255,0.16),transparent_26%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0))]" />
                <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "12px 12px" }} />
                <div className="absolute inset-x-7 top-20 h-px bg-white/12" />
                <div className="absolute inset-x-7 bottom-24 h-px bg-white/10" />

                <div className="relative flex min-h-[590px] flex-col p-7 text-white">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-white/55">
                        {cardLabels.founder_access || "Founder Access"}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#32d074]">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#32d074] shadow-[0_0_12px_rgba(50,208,116,0.9)]" />
                        {cardLabels.founder_status || "Legacy"}
                      </div>
                    </div>
                    <div className="rounded-full border border-white/10 bg-white/5 p-2 text-white/35">
                      <Wifi className="h-4 w-4" />
                    </div>
                  </div>

                  <div className="mx-auto mt-14 h-[220px] w-[170px] overflow-hidden rounded-[22px] border border-white/10 bg-white/90 p-1 shadow-xl">
                    <img
                      src={settings?.founder_photo_url || "/founder-shankar-howlader.jpg"}
                      alt={founderName}
                      className="h-full w-full rounded-xl object-cover object-top"
                    />
                  </div>

                  <div className="mt-8 text-center">
                    <Link
                      to="/founder-profile"
                      className="inline-flex rounded-xl px-2 py-1 text-left transition hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                    >
                      <span className="whitespace-nowrap font-display text-[clamp(1.1rem,1.8vw,1.45rem)] font-extrabold tracking-tight text-white">
                        {founderName}
                      </span>
                    </Link>
                    <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-cyan-400">
                      {founderTitle}
                    </div>
                  </div>

                  <div className="mt-auto flex items-end justify-between gap-5 pt-10">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.26em] text-white/35">
                        {cardLabels.first_office || "First Main Office"}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-white/80">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-cyan-400" />
                        {settings?.founder_profile_office_address || "215/3 Lalbag Road, Dhaka - 1211"}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5 pb-1">
                      <div className="flex gap-1.5">
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                      </div>
                      <div className="flex gap-1.5">
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                      </div>
                      <div className="text-right text-[11px] font-semibold uppercase tracking-[0.22em] text-white/30">
                        {ui.founderSince} {settings?.company_since || "1985"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-[30px] border border-white/10 bg-[#071017] shadow-2xl shadow-black/25">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_25%,rgba(0,209,255,0.18),transparent_32%),radial-gradient(circle_at_80%_18%,rgba(102,51,255,0.16),transparent_26%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0))]" />
                <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "12px 12px" }} />
                <div className="absolute inset-x-7 top-20 h-px bg-white/12" />
                <div className="absolute inset-x-7 bottom-24 h-px bg-white/10" />

                <div className="relative flex min-h-[590px] flex-col p-7 text-white">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-white/55">
                        {cardLabels.executive_access || "Executive Access"}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#32d074]">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#32d074] shadow-[0_0_12px_rgba(50,208,116,0.9)]" />
                        {cardLabels.executive_status || "Active"}
                      </div>
                    </div>
                    <div className="rounded-full border border-white/10 bg-white/5 p-2 text-white/35">
                      <Wifi className="h-4 w-4" />
                    </div>
                  </div>

                  <div className="mx-auto mt-14 h-[220px] w-[170px] overflow-hidden rounded-[22px] border border-white/10 bg-white/90 p-1 shadow-xl">
                    <img
                      src={settings?.ceo_photo_url || "/ceo-dibakar.png"}
                      alt={settings?.proprietor || "Engr. Dibakar Howlader"}
                      className="h-full w-full rounded-xl object-cover object-top"
                    />
                  </div>

                  <div className="mt-8 text-center">
                    <Link
                      to="/ceo-profile"
                      className="inline-flex rounded-xl px-2 py-1 text-left transition hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                    >
                      <span className="whitespace-nowrap font-display text-[clamp(1.1rem,1.8vw,1.45rem)] font-extrabold tracking-tight text-white">
                        {settings?.proprietor || "Engr. Dibakar Howlader"}
                      </span>
                    </Link>
                    <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-cyan-400">
                      {settings?.ceo_title || "Chief Executive Officer"}
                    </div>
                  </div>

                  <div className="mt-auto flex items-end justify-between gap-5 pt-10">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.26em] text-white/35">
                        {cardLabels.company_base || "Company Base"}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-2 text-sm text-white/80">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-cyan-400" />
                        {settings?.company_location || "Barishal, Bangladesh"}
                      </div>
                      <div className="mt-5 text-[10px] font-semibold uppercase tracking-[0.26em] text-white/35">
                        {cardLabels.id_label || "ID Number"}
                      </div>
                      <div className="mt-2 font-mono-plex text-base font-bold tracking-[0.08em] text-white">
                        R.S.-001
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5 pb-1">
                      <div className="flex gap-1.5">
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                      </div>
                      <div className="flex gap-1.5">
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                        <span className="h-3 w-3 rounded-sm border border-white/30 bg-white/5" />
                      </div>
                      <div className="text-right text-[11px] font-semibold uppercase tracking-[0.22em] text-white/30">
                        Since {settings?.company_since || "1985"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
